import type { CustomVariablesDataPayload, SegmentsOption } from '@openx/types/targeting/customVariables';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';

export type EnrichedSegment = {
  id: string;
  providerId: string;
  inclusionType: 'includes' | 'excludes';
  metadata: SegmentsOption;
  isMisconfigured?: boolean;
};

export type ContextualProviderSelection = {
  providerId: string;
  includes: string[];
  excludes: string[];
};

/**
 * Enriched internal state structure that stores full segment objects instead of just IDs.
 * This preserves metadata through the drawer → main form flow.
 */
export type EnrichedProviderSegments = {
  providerId: string;
  includes: SegmentsOption[];
  excludes: SegmentsOption[];
};

export type ContextualSegmentIdentity = {
  providerId: string;
  segmentId: string;
};

export type ContextualInclusionType = 'includes' | 'excludes';

export const CONTEXTUAL_INCLUSION_TYPES: ContextualInclusionType[] = ['includes', 'excludes'];

export const normalizeProviderId = (providerId: string): string => {
  const trimmedProviderId = providerId.trim();
  return trimmedProviderId.startsWith('ctx_') ? trimmedProviderId.slice(4) : trimmedProviderId;
};

const parseSegmentIds = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
    )
  );

const appendUniqueIds = (currentIds: string[], nextIds: string[]): string[] => {
  const result = [...currentIds];
  const existingIds = new Set(currentIds);

  nextIds.forEach(id => {
    if (!existingIds.has(id)) {
      existingIds.add(id);
      result.push(id);
    }
  });

  return result;
};

export const getContextualSegmentKey = ({ providerId, segmentId }: ContextualSegmentIdentity): string =>
  JSON.stringify([normalizeProviderId(providerId), segmentId]);

export const resolveContextualProviderId = (
  segment: SegmentsOption,
  accountProviderIds: string[]
): string | undefined => {
  const segmentProviderId = normalizeProviderId(segment.contextual_provider ?? '');
  if (segmentProviderId) return segmentProviderId;

  const normalizedAccountProviderIds = new Set(accountProviderIds.map(normalizeProviderId).filter(Boolean));
  return normalizedAccountProviderIds.size === 1 ? normalizedAccountProviderIds.values().next().value : undefined;
};

export const resolveBulkSegmentProvider = (
  segmentId: string,
  catalogBySegmentId: Map<string, SegmentsOption[]>,
  accountProviderIds: string[]
): { providerId: string; metadata: SegmentsOption | undefined } | undefined => {
  const normalizedAccountProviderIds = Array.from(new Set(accountProviderIds.map(normalizeProviderId).filter(Boolean)));

  // Account provider is the source of truth for bulk add
  // If exactly one account provider is configured, always use it
  if (normalizedAccountProviderIds.length === 1) {
    const accountProviderId = normalizedAccountProviderIds[0];
    // Try to find metadata for this segment under the account provider
    const catalogMatches = catalogBySegmentId.get(segmentId) ?? [];
    const metadata = catalogMatches.find(
      segment => normalizeProviderId(segment.contextual_provider ?? '') === accountProviderId
    );
    return { providerId: accountProviderId, metadata };
  }

  // If no account provider or multiple providers, reject (shouldn't happen in current UI)
  return undefined;
};

/**
 * Groups the custom format per provider, keeping an id excluded even when it is also included:
 * the backend ANDs both lists, so such an exclusion still narrows the targeting.
 */
export const groupCustomFormatByProvider = (
  customData: CustomVariablesDataPayload[]
): ContextualProviderSelection[] => {
  const selections: ContextualProviderSelection[] = [];
  const selectionByProvider = new Map<string, ContextualProviderSelection>();

  customData.forEach(entry => {
    const providerId = normalizeProviderId(entry.attr);
    if (!providerId) return;
    if (entry.op !== ComparisonType.INTERSECTS && entry.op !== ComparisonType.NOT_INTERSECTS) return;

    let selection = selectionByProvider.get(providerId);
    if (!selection) {
      selection = { providerId, includes: [], excludes: [] };
      selectionByProvider.set(providerId, selection);
      selections.push(selection);
    }

    const inclusionType = entry.op === ComparisonType.INTERSECTS ? 'includes' : 'excludes';
    selection[inclusionType] = appendUniqueIds(selection[inclusionType], parseSegmentIds(entry.val));
  });

  return selections;
};

export const parseCustomFormatToProviderSelections = (
  customData: CustomVariablesDataPayload[]
): ContextualProviderSelection[] => {
  const selections = groupCustomFormatByProvider(customData);

  selections.forEach(selection => {
    const includedIds = new Set(selection.includes);
    selection.excludes = selection.excludes.filter(id => !includedIds.has(id));
  });

  return selections;
};

export const getSelectedSegmentIds = (customData: CustomVariablesDataPayload[]): string[] => {
  const ids = new Set<string>();

  parseCustomFormatToProviderSelections(customData).forEach(selection => {
    selection.includes.forEach(id => {
      ids.add(id);
    });
    selection.excludes.forEach(id => {
      ids.add(id);
    });
  });

  return Array.from(ids);
};

export const providerSelectionsToCustomFormat = (
  selections: ContextualProviderSelection[]
): CustomVariablesDataPayload[] => {
  const result: CustomVariablesDataPayload[] = [];

  selections.forEach(selection => {
    const providerId = normalizeProviderId(selection.providerId);
    if (!providerId) return;

    const includes = parseSegmentIds(selection.includes.join(','));
    const includedIds = new Set(includes);
    const excludes = parseSegmentIds(selection.excludes.join(',')).filter(id => !includedIds.has(id));

    if (includes.length > 0) {
      result.push({ attr: providerId, op: ComparisonType.INTERSECTS, val: includes.join(',') });
    }

    if (excludes.length > 0) {
      result.push({ attr: providerId, op: ComparisonType.NOT_INTERSECTS, val: excludes.join(',') });
    }
  });

  return result;
};

export const setContextualSegmentInclusion = (
  selections: ContextualProviderSelection[],
  identity: ContextualSegmentIdentity,
  inclusionType: ContextualInclusionType
): ContextualProviderSelection[] => {
  const providerId = normalizeProviderId(identity.providerId);
  if (!providerId) return selections;

  const oppositeInclusionType = inclusionType === 'includes' ? 'excludes' : 'includes';
  const existingSelection = selections.find(selection => normalizeProviderId(selection.providerId) === providerId);

  if (!existingSelection) {
    return [
      ...selections,
      {
        providerId,
        includes: inclusionType === 'includes' ? [identity.segmentId] : [],
        excludes: inclusionType === 'excludes' ? [identity.segmentId] : [],
      },
    ];
  }

  return selections.map(selection => {
    if (normalizeProviderId(selection.providerId) !== providerId) return selection;

    return {
      ...selection,
      [oppositeInclusionType]: selection[oppositeInclusionType].filter(id => id !== identity.segmentId),
      [inclusionType]: selection[inclusionType].includes(identity.segmentId)
        ? selection[inclusionType]
        : [...selection[inclusionType], identity.segmentId],
    };
  });
};

export const removeContextualSegment = (
  selections: ContextualProviderSelection[],
  identity: ContextualSegmentIdentity
): ContextualProviderSelection[] => {
  const providerId = normalizeProviderId(identity.providerId);

  return selections
    .map(selection => {
      if (normalizeProviderId(selection.providerId) !== providerId) return selection;

      return {
        ...selection,
        includes: selection.includes.filter(id => id !== identity.segmentId),
        excludes: selection.excludes.filter(id => id !== identity.segmentId),
      };
    })
    .filter(selection => selection.includes.length > 0 || selection.excludes.length > 0);
};

export const enrichSegmentsWithMetadata = (
  customData: CustomVariablesDataPayload[],
  segmentsCache: SegmentsOption[],
  accountProviderIds: string[] = []
): EnrichedSegment[] => {
  const segmentMap = new Map<string, SegmentsOption>();
  const segmentsById = new Map<string, SegmentsOption[]>();
  const providerNamesMap: Record<string, string> = {};

  segmentsCache.forEach(segment => {
    const providerId = resolveContextualProviderId(segment, accountProviderIds);
    if (providerId) {
      segmentMap.set(getContextualSegmentKey({ providerId, segmentId: segment.id }), segment);
      if (segment.provider_name && !providerNamesMap[providerId]) {
        providerNamesMap[providerId] = segment.provider_name;
      }
    }
    segmentsById.set(segment.id, [...(segmentsById.get(segment.id) ?? []), segment]);
  });

  const result: EnrichedSegment[] = [];
  for (const selection of parseCustomFormatToProviderSelections(customData)) {
    for (const inclusionType of ['includes', 'excludes'] as const) {
      for (const segmentId of selection[inclusionType]) {
        const metadata =
          segmentMap.get(getContextualSegmentKey({ providerId: selection.providerId, segmentId })) ??
          // Legacy responses omit provider IDs; an ID-only fallback is safe only when the ID is globally unique.
          (segmentsById.get(segmentId)?.length === 1 ? segmentsById.get(segmentId)?.[0] : undefined);

        result.push({
          id: segmentId,
          providerId: selection.providerId,
          inclusionType,
          metadata: metadata || {
            id: segmentId,
            name: segmentId,
            provider_name: providerNamesMap[normalizeProviderId(selection.providerId)] ?? '',
            contextual_provider: selection.providerId,
          },
        });
      }
    }
  }

  return result;
};

/**
 * Builds a map of provider IDs to provider names from catalog segments and optional single provider fallback.
 */
export const buildProviderNamesMap = (
  segments: SegmentsOption[],
  accountProviderIds: string[],
  singleProviderName?: string
): Record<string, string> => {
  const map: Record<string, string> = {};
  const normalizedAccountProviderIds = new Set(accountProviderIds.map(normalizeProviderId).filter(Boolean));
  const soleProviderId = accountProviderIds.length === 1 ? normalizeProviderId(accountProviderIds[0]) : undefined;

  // When there's a single provider with a known name from the API, use that as the source of truth
  // The API provider name is more reliable than catalog data which can be corrupted
  if (soleProviderId && singleProviderName) {
    map[soleProviderId] = singleProviderName;
    return map;
  }

  // For multiple providers or when no API name is available, extract from catalog segments
  segments.forEach(segment => {
    const segmentProviderId = normalizeProviderId(segment.contextual_provider ?? '');
    if (!segmentProviderId || !segment.provider_name || map[segmentProviderId]) {
      return;
    }

    // On details/edit pages, account may have no providers but we still have persisted segments
    // Extract provider names from catalog even when not in accountProviderIds for better display
    if (normalizedAccountProviderIds.size === 0 || normalizedAccountProviderIds.has(segmentProviderId)) {
      map[segmentProviderId] = segment.provider_name;
    }
  });

  return map;
};

export const buildProviderScopedSegmentsMap = (
  segments: SegmentsOption[],
  accountProviderIds: string[],
  providerNamesMap: Record<string, string>
): Record<string, SegmentsOption> => {
  const normalizedAccountProviderIds = new Set(accountProviderIds.map(normalizeProviderId).filter(Boolean));
  const soleProviderId =
    normalizedAccountProviderIds.size === 1 ? normalizedAccountProviderIds.values().next().value : undefined;
  const soleProviderName = soleProviderId ? providerNamesMap[soleProviderId] : undefined;

  return segments.reduce(
    (result, segment) => {
      const segmentProviderId = normalizeProviderId(segment.contextual_provider ?? '');

      if (segmentProviderId) {
        result[getContextualSegmentKey({ providerId: segmentProviderId, segmentId: segment.id })] = segment;
      } else if (!segmentProviderId && soleProviderId) {
        const hasNoProviderName = !segment.provider_name;
        const hasMatchingProviderName = soleProviderName && segment.provider_name === soleProviderName;

        if (hasNoProviderName || hasMatchingProviderName) {
          result[getContextualSegmentKey({ providerId: soleProviderId, segmentId: segment.id })] = segment;
        }
      }
      return result;
    },
    {} as Record<string, SegmentsOption>
  );
};

export const groupSegmentsByInclusionType = (
  enrichedSegments: EnrichedSegment[]
): {
  includes: EnrichedSegment[];
  excludes: EnrichedSegment[];
} => {
  const includes = enrichedSegments.filter(seg => seg.inclusionType === 'includes');
  const excludes = enrichedSegments.filter(seg => seg.inclusionType === 'excludes');

  return { includes, excludes };
};

/**
 * Converts API format (CustomVariablesDataPayload[]) to enriched segments format.
 * Looks up segment metadata from catalog and builds full segment objects.
 */
export const customFormatToEnrichedSegments = (
  apiData: CustomVariablesDataPayload[],
  catalogSegments: SegmentsOption[],
  accountProviderIds: string[],
  providerNamesMap: Record<string, string>
): EnrichedProviderSegments[] => {
  const segmentsMap = buildProviderScopedSegmentsMap(catalogSegments, accountProviderIds, providerNamesMap);

  // Group by provider
  const providerMap = new Map<string, { includes: SegmentsOption[]; excludes: SegmentsOption[] }>();

  apiData.forEach(item => {
    const providerId = normalizeProviderId(item.attr);
    if (!providerId) return;

    const fallbackProviderName = providerNamesMap[providerId] || providerId;
    const isInclude = item.op === ComparisonType.INTERSECTS;
    const segmentIds = parseSegmentIds(item.val);

    const enrichedSegments = segmentIds.map(segmentId => {
      const lookupKey = getContextualSegmentKey({ providerId, segmentId });
      const foundSegment = segmentsMap[lookupKey];

      if (foundSegment) {
        return {
          ...foundSegment,
          provider_name: fallbackProviderName,
          contextual_provider: providerId,
        };
      }

      // Fallback: create segment with ID as name
      return {
        id: segmentId,
        name: segmentId,
        provider_name: fallbackProviderName,
        contextual_provider: providerId,
      };
    });

    const existing = providerMap.get(providerId) || { includes: [], excludes: [] };
    if (isInclude) {
      existing.includes.push(...enrichedSegments);
    } else {
      existing.excludes.push(...enrichedSegments);
    }
    providerMap.set(providerId, existing);
  });

  return Array.from(providerMap.entries()).map(([providerId, segments]) => ({
    providerId,
    includes: segments.includes,
    excludes: segments.excludes,
  }));
};

/**
 * Converts enriched segments format to API format (CustomVariablesDataPayload[]).
 * Serializes full segment objects back to comma-separated IDs.
 */
export const enrichedSegmentsToCustomFormat = (
  enrichedSegments: EnrichedProviderSegments[]
): CustomVariablesDataPayload[] => {
  const result: CustomVariablesDataPayload[] = [];

  enrichedSegments.forEach(selection => {
    const providerId = normalizeProviderId(selection.providerId);
    if (!providerId) return;

    const includeIds = Array.from(new Set(selection.includes.map(segment => segment.id)));
    const excludeIds = Array.from(new Set(selection.excludes.map(segment => segment.id)));

    // Remove excludes that are also in includes
    const includedIdsSet = new Set(includeIds);
    const filteredExcludeIds = excludeIds.filter(id => !includedIdsSet.has(id));

    if (includeIds.length > 0) {
      result.push({ attr: providerId, op: ComparisonType.INTERSECTS, val: includeIds.join(',') });
    }

    if (filteredExcludeIds.length > 0) {
      result.push({ attr: providerId, op: ComparisonType.NOT_INTERSECTS, val: filteredExcludeIds.join(',') });
    }
  });

  return result;
};

/**
 * Adds or moves a segment to a specific inclusion type in enriched provider segments.
 */
export const setEnrichedSegmentInclusion = (
  selections: EnrichedProviderSegments[],
  segment: SegmentsOption,
  inclusionType: ContextualInclusionType,
  providerId: string
): EnrichedProviderSegments[] => {
  const normalizedProviderId = normalizeProviderId(providerId);
  if (!normalizedProviderId) return selections;

  const oppositeInclusionType = inclusionType === 'includes' ? 'excludes' : 'includes';
  const existingSelection = selections.find(sel => normalizeProviderId(sel.providerId) === normalizedProviderId);

  if (!existingSelection) {
    return [
      ...selections,
      {
        providerId: normalizedProviderId,
        includes: inclusionType === 'includes' ? [segment] : [],
        excludes: inclusionType === 'excludes' ? [segment] : [],
      },
    ];
  }

  return selections.map(selection => {
    if (normalizeProviderId(selection.providerId) !== normalizedProviderId) return selection;

    return {
      ...selection,
      [oppositeInclusionType]: selection[oppositeInclusionType].filter(seg => seg.id !== segment.id),
      [inclusionType]: selection[inclusionType].some(seg => seg.id === segment.id)
        ? selection[inclusionType]
        : [...selection[inclusionType], segment],
    };
  });
};

/**
 * Removes a segment from enriched provider segments.
 */
export const removeEnrichedSegment = (
  selections: EnrichedProviderSegments[],
  segmentId: string,
  providerId: string
): EnrichedProviderSegments[] => {
  const normalizedProviderId = normalizeProviderId(providerId);

  return selections
    .map(selection => {
      if (normalizeProviderId(selection.providerId) !== normalizedProviderId) return selection;

      return {
        ...selection,
        includes: selection.includes.filter(seg => seg.id !== segmentId),
        excludes: selection.excludes.filter(seg => seg.id !== segmentId),
      };
    })
    .filter(selection => selection.includes.length > 0 || selection.excludes.length > 0);
};
